import { Matrix4, Object3D, PerspectiveCamera, Vector3 } from "three";
import { maxHeight, wallLength } from "../controllers/wall-controllers";
import { Wall } from "../domain";

// A scratch object used as an ad hoc transformation stack
const obj = new Object3D();

/**
 * Positions and rotates a camera to frame an isolated wall within the camera's
 * field of view. This makes the camera a child of the wall for the sake of
 * simplicity.
 *
 * Note that if you change the camera's fov, you may need to call
 * camera.updateProjectionMatrix(). This method only re-positions the camera,
 * so I don't update the projection matrix here.
 *
 * @param camera - the camera to modify
 * @param wall - the wall to look at
 * @param margin - amount of additional space above/below or left/right of the
 * wall to include in the view
 * @param target - if given, copies the center of the wall to the target vector
 * so that the orbit navigation remains consistent.
 */
const lookAtWall = (
    camera: PerspectiveCamera,
    wall: Wall,
    margin: number = 12,
    target?: Vector3,
) =>
{
    const
        length = wall.showExtension
            ? wallLength(wall) + wall.leftExtension + wall.rightExtension
            : wallLength(wall),
        halfWidth = length / 2,
        halfHeight = maxHeight(wall) / 2,
        // hvFov is half the camera.fov converted from degrees to radians
        hvFov = camera.fov * Math.PI / 360,
        hhFov = Math.atan(Math.tan(hvFov) * camera.aspect),
        z = Math.max(
            (halfHeight + margin/2) / Math.tan(hvFov),
            (halfWidth + margin/2) / Math.tan(hhFov)
        );
    
    obj.position.copy(wall.position);
    obj.rotation.set(wall.rotation.x, wall.rotation.y, wall.rotation.z);
    obj.updateMatrixWorld();

    camera.position.x = halfWidth;
    camera.position.y = halfHeight;
    camera.position.z = z;
    camera.rotation.set(0, 0, 0);

    camera.applyMatrix4( obj.matrixWorld );

    if(target) {
        target.set(halfWidth, halfHeight, 0);
        obj.localToWorld(target);
    }
};
export default lookAtWall;
